import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/constants/siteData";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#e5e7eb]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-page flex h-20 md:h-28 items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center"
          aria-label="DigiTrans Technology — home"
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/digitrans-logo.png`}
            alt="DigiTrans Technology"
            className="h-14 md:h-20 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide link-underline transition-colors ${
                  isActive ? "text-[#1E5AF6]" : "text-[#0B1220] hover:text-[#1E5AF6]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            data-testid="nav-cta-connect"
            href={`mailto:${COMPANY.email}?subject=Discovery`}
            className="group relative shine inline-flex items-center gap-2 bg-[#0B1220] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#1E5AF6] transition-colors box-elevated"
          >
            Connect
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center border border-[#e5e7eb]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#e5e7eb] bg-white" data-testid="nav-mobile-menu">
          <div className="container-page py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-mobile-link-${l.id}`}
                className={({ isActive }) =>
                  `text-lg font-display font-semibold ${
                    isActive ? "text-[#1E5AF6]" : "text-[#0B1220]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`mailto:${COMPANY.email}?subject=Discovery`}
              data-testid="nav-mobile-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#0B1220] text-white px-5 py-3 text-sm font-semibold"
            >
              Connect <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
